import { Logo } from '../brand/Logo';
import { CoffeeIcon, HeartIcon } from '../ui/Icons';
import { footer } from '../../content/site';
import { externalLinkAttrs, sanitizeHref } from '../../lib/url';
import { usePendingLink } from '../ui/ComingSoon';

export function Footer() {
  const pendingLink = usePendingLink();

  return (
    <footer className="bg-black pt-20 pb-8">
      <div className="shell">
        <div className="flex flex-wrap gap-x-16 gap-y-12">
          <div className="min-w-[280px] max-w-[360px] flex-1">
            <span className="text-[26px] leading-none">
              <Logo variant="white" />
            </span>
            <p className="mt-5 text-[14.5px] leading-[1.7] text-ondark-soft">
              {footer.tagline}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              {footer.stores.map((store) => (
                <a
                  key={store.label}
                  href={sanitizeHref(store.href)}
                  {...externalLinkAttrs(store.href)}
                  {...pendingLink(store.href, store.label)}
                  aria-label={store.label}
                  className="inline-flex rounded-[8px] transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  <img
                    src={store.src}
                    alt={store.label}
                    width={store.width}
                    height={store.height}
                    loading="lazy"
                    decoding="async"
                    // El arte de Google incluye aire vertical propio; se absorbe
                    // con margen negativo para alinear ambos badges sin recortar.
                    className={`w-auto ${store.imgClass}`}
                  />
                </a>
              ))}
            </div>
          </div>

          {footer.columns.map((column) => (
            <nav
              key={column.title}
              aria-label={column.title}
              className="min-w-[150px]"
            >
              <h2 className="font-display text-[11px] font-semibold tracking-[0.22em] text-white normal-case">
                {column.title}
              </h2>
              <ul className="mt-5 flex flex-col gap-3">
                {column.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={sanitizeHref(link.href)}
                      {...externalLinkAttrs(link.href)}
                      {...pendingLink(link.href, link.label)}
                      className="text-[14.5px] text-ondark-soft transition-colors hover:text-white"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          <div className="min-w-[200px]">
            <h2 className="font-display text-[11px] font-semibold tracking-[0.22em] text-white normal-case">
              {footer.contact.title}
            </h2>
            <ul className="mt-5 flex flex-col gap-3 text-[14.5px] text-ondark-soft">
              <li>
                <a
                  href={`mailto:${footer.contact.email}`}
                  className="transition-colors hover:text-white"
                >
                  {footer.contact.email}
                </a>
              </li>
              <li>{footer.contact.address}</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap justify-between gap-4 border-t border-line-darker pt-7 text-[13px] text-ondark-soft">
          <p>{footer.legal}</p>
          <p className="credit flex flex-wrap items-center gap-x-1.5">
            {footer.madeBy.lead}
            {/* Rojo de un solo uso: la paleta de marca es azul y dorada, y no
                tiene un rojo que pintar aquí. */}
            <HeartIcon
              className="credit-heart h-[15px] w-[15px] text-[#e0575b]"
              title={footer.madeBy.loveLabel}
            />
            {footer.madeBy.conjunction}
            <CoffeeIcon
              className="credit-cup h-[15px] w-[15px] text-brand-gold"
              title={footer.madeBy.coffeeLabel}
            />
            {footer.madeBy.by}
            <a
              href={sanitizeHref(footer.madeBy.href)}
              {...externalLinkAttrs(footer.madeBy.href)}
              // El nombre se parte en letras para la ola, así que se declara
              // aquí completo: sin esto un lector de pantalla lo deletrearía.
              aria-label={footer.madeBy.company}
              className="credit-link"
            >
              {[...footer.madeBy.company].map((letter, i) => (
                <span
                  key={`${letter}-${i}`}
                  aria-hidden="true"
                  className={`credit-letter wave-${i % 10}`}
                >
                  {letter === ' ' ? ' ' : letter}
                </span>
              ))}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
